<template>
  <div class="explore pb-5" id="explore-section">
    <h2 class="heading-head text-center pt-5 pb-5 fw-bold">
      EXPLORE YOUR MILITARY EDUCATION OPTIONS
    </h2>

    <tabs :mode="mode" class="container">
      <tab title="OPME">
        <div class="container mx-auto pb-5 px-0">
          <h3 class="font24 pt-2 pb-2 fw-bold text-start">Officer Professional Military Education</h3>
          <div style="display: flex; overflow-x: auto">
            <div v-for="(item, index) in opmeItems" :key="index"
              class="flex-wrapper explore-each col-xl-3 col-lg-3 col-md-5 col-sm-10 col-10 text-start my-2 pl-0 pe-3 explore-card">
              <div class="d-flex flex-column pt-5 pb-1-7 px-3 col-border w-100 justify-content-between">
                <div class="mb-5">
                  <p class="mb-4 font24 fw-bold">{{ item.title }}</p>
                  <p class="mini-font d-none d-md-block">{{ item.desc }}</p>

                  <img v-if="item.badge" :src="getImageUrl(item.badge)"
                    class="grey-section-logo align-self-left mb-1 h-48 d-none d-md-block" style="width: fit-content" :alt="item.alt" />
                  <p class="font9 d-none d-md-block mb-1" v-if="item.note">{{ item.note }}</p>
                  <p class="font9 d-none d-md-block " v-if="item.sup">{{ item.sup }}</p>
                </div>

                <a
                  class="btn my-2 my-sm-0 apply-btn-1 d-flex learn-more"
                  href="https://www.airuniversity.af.edu/GCPME/"
                  target="_blank"
                  rel="noopener noreferrer"
                  tabindex="0"
                >
                  Learn more
                </a>
              </div>
            </div>
          </div>
        </div>

      </tab>

      <tab title="EPME">
        <div class="container mx-auto pb-5 px-0">
          <h3 class="font24 pt-2 pb-2 fw-bold text-start">Enlisted Professional Military Education</h3>
          <div style="display: flex; overflow-x: auto">
            <div v-for="(item, index) in epmeItems" :key="index"
              class="flex-wrapper explore-each col-xl-3 col-lg-3 col-md-5 col-sm-10 col-10 text-start my-2 pl-0 pe-3 explore-card">
              <div class="d-flex flex-column pt-5 pb-1-7 px-3 col-border w-100 justify-content-between">
                <div class="mb-5">
                  <p class="mb-4 font24 fw-bold">{{ item.title }}</p>
                  <p class="mini-font mb-5 d-none d-md-block">{{ item.desc }}</p>
                </div>

                <a
                  class="btn my-2 my-sm-0 apply-btn-1 d-flex learn-more"
                  href="https://www.airuniversity.af.edu/GCPME/"
                  target="_blank"
                  rel="noopener noreferrer"
                  tabindex="0"
                >
                  Learn more
                </a>
              </div>
            </div>
          </div>
        </div>
      </tab>
    </tabs>

    <div id="my-content" class="d-md-none d-block px-4 mx-2 text-start font18">
      Scroll for more
      <img :src="getImageUrl('arrow-right-solid.svg')" height="15" />
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import Tab from '@/components/tab.vue';
import Tabs from '@/components/tabs.vue';

const mode = ref('light');

// Webpack-compatible dynamic import
const imageContext = require.context('@/assets/images', false, /\.(png|jpe?g|svg|webp)$/)

function getImageUrl(fileName) {
  if (!fileName) return '';
  try {
    return imageContext(`./${fileName}`)
  } catch (e) {
    console.error(`Image "${fileName}" not found in assets/images`)
    return ''
  }
}

const opmeItems = ref([
  {
    title: "Squadron Officer School",
    desc: "The Air Force Global College’s Squadron Officer School distance learning program is a primary developmental education program. It also helps prepare students for leadership within the squadron level and builds critical thinking skills in the application of air and space power."
  },
  {
    title: "Online Master’s Program",
    desc: "The Online Master’s Program is an intermediate developmental education opportunity designed to produce highly effective officers for operational-level command or staff positions. The degree program is offered in four different concentrations: joint warfare, leadership, operational warfare and nuclear weapons.",
    badge: "JCSBadge.png",
    note: "JPME Phase 1 Credit Awarded*",
    sup: "*Joint Warfare concentration only",
    alt: "JPME Phase 1 Credit Awarded*",
  },
  {
    title: "Air Command and Staff College",
    desc: "The Air Force Global College’s Air Command and Staff College distance learning intermediate developmental education curriculum is designed to produce a more effective field-grade officer serving in operational-level command or staff positions.",
    badge: "JCSBadge.png",
    note: "JPME Phase 1 Credit Awarded",
    alt: "JPME Phase 1 Credit Awarded",
  },
  {
    title: "Air War College",
    desc: "The Air Force Global College’s Air War College distance learning program is a senior developmental education program. It provides a curriculum that prepares graduates for strategic leadership in support of national security objectives."
  }
]);

const epmeItems = ref([
  {
    title: "Airman Leadership School",
    desc: "Airman Leadership School (ALS) is an education program held at base level to prepare Senior Airmen for positions of greater responsibility. The course teaches leadership skills required of supervisors and reporting officials throughout the Air and Space Forces. ALS enhances the development of Senior Airmen by strengthening their ability to lead, follow, and manage while they gain a broader understanding of the military profession. ALS is attended by Senior Airmen (E-4) and required for promotion to Staff Sergeant (E-5).",
    modalId: "exampleModal7"
  },
  {
    title: "Noncommissioned Officer Academy (NCOA)",
    desc: "The Noncommissioned Officer Academy (NCOA) is the second level of enlisted PME and prepares Technical Sergeants to be professional, war-fighting Airmen and Space Professionals who can manage and lead units in the employment of Air and Space power.",
    modalId: "exampleModal8"
  },
  {
    title: "Senior Noncommissioned Officer Academy (SNCOA)",
    desc: "The Air Force Senior Noncommissioned Officer Academy (SNCOA) is the third level of enlisted PME. SNCOA prepares senior NCOs to lead the enlisted force in the employment of AirPower in support of US national security objectives.",
    modalId: "exampleModal9"
  }
]);
</script>

<style lang="scss">
#explore-section {
  .tab-buttons {
    display: flex;
    margin-bottom: 1rem;
    border-bottom: 2px solid rgba(255, 255, 255, 1);

    button {
      padding: 15px 30px !important;
      border-radius: 10px !important;
      margin: 0 !important;
      display: inline-block !important;
      margin-right: 5px !important;
      cursor: pointer !important;
      font-size: 24px !important;
      border-bottom-width: 10px !important;
    }
  }

  .offcanvas {
    max-width: 500px !important;
    width: 100vw !important;

    .offcanvas-header {
      left: 0px !important;
      right: 0px !important;
    }
  }

}
</style>
<style>
.explore-card {
  border-radius: 4px;
  color: white;
  display: flex;
  justify-content: space-between;
  text-align: left;
}

.font9 {
  font-size: 9px !important;
}



.card-row {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  gap: 2rem;
  padding-bottom: 2rem;
  padding-top: 1rem;
}

.explore-card p {
  font-size: 14px;
}

.explore-card .font24 {
  font-size: 20px;
  font-weight: bold;
}

.explore-card img {
  height: 48px;
  margin-top: auto;
}

.apply-btn-1 {
  background: none;
  color: white;
  border: 2px solid white;
  padding: 0.5rem 1.5rem;
  border-radius: 30px;
  margin-top: 1.5rem;
  align-self: flex-start;
  font-weight: bold;
}

.apply-btn-1:hover {
  background: white;
  color: black;
}

.close-icon {
  padding-right: 5px !important;

}

.close {
  cursor: pointer;
  text-shadow: 0 1px 0 #fff;
}

.pb-1-7 {
  padding-bottom: 1.7rem !important;
}

.modal-p {
  font-size: 16px !important;
}

.mdal-list {
  font-size: 16px !important;
}
</style>